import ApprovalIcon from "@mui/icons-material/Approval";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Kks } from "@shared/types/domain";

export function KksEditor({
  kks,
  index,
  total,
  onChange,
  onDelete,
  shortcutsEnabled,
  componentOptions,
  componentDisabled = false,
  locked,
  frozenRequiredSpof = false,
  disableRequiredSpof = false,
}: {
  kks: Kks;
  index: number;
  total: number;
  onChange: (updates: Partial<Kks>) => void;
  onDelete: () => void;
  shortcutsEnabled: boolean;
  componentOptions: string[];
  /** When true, the component combobox is disabled (no sub-system selected yet). */
  componentDisabled?: boolean;
  locked: boolean;
  /** When true, all fields are read-only (KKS is required_spof-locked). */
  frozenRequiredSpof?: boolean;
  /** When true, the Required SPOF checkbox is individually disabled (status ≥ verification and not super_user). */
  disableRequiredSpof?: boolean;
}) {
  // Effective lock: full PID lock OR required_spof frozen
  const effectiveLocked = locked || frozenRequiredSpof;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        ...(effectiveLocked && {
          opacity: 0.45,
          pointerEvents: "none",
          ...(frozenRequiredSpof &&
            !locked && { outline: "2px solid #AB47BC", borderRadius: 2 }),
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "#5C6B7A",
            fontSize: "0.65rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
          }}
        >
          KKS {index + 1} of {total}
        </Typography>
        <Tooltip title="Delete KKS">
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            sx={{ p: 0.5 }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <TextField
          label="Text"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={kks.text}
          onChange={(e) => onChange({ text: e.target.value })}
          sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <Tooltip
          title={
            kks.stamp_on_pdf
              ? shortcutsEnabled
                ? "Remove stamp (p)"
                : "Remove stamp"
              : shortcutsEnabled
                ? "Stamp on PDF (p)"
                : "Stamp on PDF"
          }
        >
          <IconButton
            size="small"
            onClick={() => onChange({ stamp_on_pdf: !kks.stamp_on_pdf })}
            sx={{
              mt: 0.5,
              color: kks.stamp_on_pdf ? "#000000" : "#bdbdbd",
              bgcolor: kks.stamp_on_pdf ? "rgba(0,0,0,0.10)" : "transparent",
              borderRadius: 1.5,
              "&:hover": {
                bgcolor: kks.stamp_on_pdf
                  ? "rgba(0,0,0,0.18)"
                  : "rgba(0,0,0,0.06)",
              },
            }}
          >
            <ApprovalIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Autocomplete
        size="small"
        options={componentOptions}
        value={kks.component === "Unknown" ? null : kks.component}
        disabled={componentDisabled}
        onChange={(_e, v) => onChange({ component: v ?? "Unknown" })}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              componentDisabled ? "Select a sub-system first…" : "Component"
            }
            placeholder={
              componentDisabled
                ? "Select a sub-system first…"
                : "Select component…"
            }
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        )}
        disableClearable={false}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
        {(
          [
            {
              field: "required",
              label: shortcutsEnabled ? "Required (r)" : "Required",
              checked: kks.required,
              disabled: false,
            },
            {
              field: "verified",
              label: shortcutsEnabled ? "Verified (v)" : "Verified",
              checked: kks.verified,
              disabled: false,
            },
            {
              field: "spof",
              label: shortcutsEnabled ? "SPOF (s)" : "SPOF",
              checked: kks.spof,
              disabled: false,
            },
            {
              field: "required_spof",
              label: shortcutsEnabled ? "Req. SPOF (q)" : "Required SPOF",
              checked: kks.required_spof,
              disabled: !kks.spof || disableRequiredSpof,
            },
            {
              field: "spof_approved",
              label: shortcutsEnabled ? "SPOF Approved (a)" : "SPOF Approved",
              checked: kks.spof_approved,
              disabled: !kks.spof,
            },
          ] as const
        ).map(({ field, label, checked, disabled }) => (
          <FormControlLabel
            key={field}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) =>
                  !disabled && onChange({ [field]: e.target.checked })
                }
                disabled={disabled}
                size="small"
                sx={{ py: 0.25 }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: disabled ? "#C4CDD8" : undefined,
                }}
              >
                {label}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        ))}
      </Box>

      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "#888",
            fontWeight: 600,
            letterSpacing: "0.05em",
            mb: 0.5,
            display: "block",
          }}
        >
          IMPACT
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={kks.impact}
          onChange={(_, val) => val !== null && onChange({ impact: val })}
          size="small"
        >
          {(
            [
              { value: 1, label: "1", color: "#0284c7", bg: "#e0f2fe" },
              { value: 2, label: "2", color: "#7c3aed", bg: "#ede9fe" },
              { value: 3, label: "3", color: "#d97706", bg: "#fef3c7" },
              { value: 4, label: "4", color: "#dc2626", bg: "#fee2e2" },
            ] as const
          ).map(({ value, label, color, bg }) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{
                fontWeight: kks.impact === value ? 800 : "bold",
                fontSize: "0.75rem",
                px: 1.5,
                color: kks.impact === value ? "#fff" : color,
                bgcolor: kks.impact === value ? `${color} !important` : bg,
                border:
                  kks.impact === value
                    ? `2px solid ${color} !important`
                    : `1px solid ${color} !important`,
                opacity: kks.impact === value ? 1 : 0.45,
                "&:hover": { bgcolor: bg, opacity: 0.85 },
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" sx={{ color: "#8E99A8" }}>
        Page {kks.page ?? "?"} · Confidence:{" "}
        {kks.confidence !== null
          ? `${(kks.confidence * 100).toFixed(0)}%`
          : "—"}
      </Typography>
    </Box>
  );
}
